// Blog / Explorer — UNIFIED editorial index.
//
// Every content type geoVINUM publishes lives here in ONE grid:
//   • Blog articles                (source: /api/blog/articles)
//   • Atlas Appellations           (source: /api/public/appellations)
//   • Atlas Cépages                (source: /api/public/cepages)
//   • Atlas Producteurs            (source: /api/public/producers)
//
// A single search bar filters everything by title. Tag chips filter by kind
// or by editorial category. Each card carries a cover photo (Wikimedia
// Commons for Atlas fiches via <CoverImage>, or the article's own cover for
// blog posts). Every item is a candidate for CommandBar (⌘K) — see
// backend `/api/search` which now indexes atlas fiches as well.
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Clock, FileText, MapPin, Grape, Building2, X, ArrowDownUp, Send, Check } from "lucide-react";
import Layout, { Breadcrumb } from "@/components/Layout";
import SEO from "@/components/SEO";
import CoverImage from "@/components/CoverImage";
import { useLang, useT } from "@/lib/i18n";
import { apiFetch } from "@/lib/api";

const CATEGORY_SLUGS = ["wine_insights", "guides", "get_started", "for_professionals", "ebook"];

// Explorer hero — inline i18n dict. Kept here (not in i18n.jsx) to keep
// the copy edit surface tight while the marketing team iterates on
// wording. Mirrors the FR/EN/ES/PT structure used elsewhere on Home.
const EXPLORER_HERO = {
  fr: {
    title: "Explorer",
    subtitle: "Le carnet de bord du vin par geoVINUM. Toute la géographie viticole du monde (régions, vignobles, appellations), les cépages et leurs profils aromatiques, les producteurs à suivre et les articles pour progresser à votre rythme — plus de 600 fiches, mises à jour au fil des découvertes.",
  },
  en: {
    title: "Explorer",
    subtitle: "geoVINUM's wine logbook. All the world's wine geography (regions, vineyards, appellations), grape varieties and their aromatic profiles, producers to follow and articles to progress at your own pace — more than 600 fiches, updated as we discover them.",
  },
  es: {
    title: "Explorador",
    subtitle: "El cuaderno de bitácora del vino de geoVINUM. Toda la geografía vitivinícola del mundo (regiones, viñedos, denominaciones), las variedades y sus perfiles aromáticos, los productores a seguir y los artículos para progresar a tu ritmo — más de 600 fichas, actualizadas al hilo de los descubrimientos.",
  },
  pt: {
    title: "Explorador",
    subtitle: "O diário de bordo do vinho da geoVINUM. Toda a geografia vinícola do mundo (regiões, vinhedos, denominações), as castas e os seus perfis aromáticos, os produtores a seguir e os artigos para progredir ao teu ritmo — mais de 600 fichas, atualizadas ao ritmo das descobertas.",
  },
};

// Kind identifiers used by both the URL `cat` param and the internal item shape.
const KIND_APPELLATION = "appellation";
const KIND_CEPAGE = "cepage";
const KIND_PRODUCER = "producer";

// Local i18n fallbacks — the main i18n.jsx dictionary is huge (~6300 lines)
// and we don't want to grow it for these few strings. `t()` returns the raw
// key when a translation is missing, which is truthy, so `t(x) || fallback`
// wouldn't work. `T(t, key, fallback)` picks the fallback when `t` echoes
// the key back.
const T = (t, key, fallback) => {
  const v = t(key);
  return v && v !== key ? v : fallback;
};

const LOCAL_STRINGS = {
  fr: {
    intro: "Insights, guides, appellations, cépages et producteurs — toute la matière geoVINUM au même endroit.",
    placeholder: "Rechercher un article, une appellation, un cépage, un producteur…",
    all: "Tout",
    appellations: "Appellations",
    cepages: "Cépages",
    producers: "Producteurs",
    results: (n) => `${n} résultat${n > 1 ? "s" : ""}`,
    no_query: (q) => `Aucun résultat pour « ${q} »`,
    sort_label: "Trier par",
    sort_recent: "Récent",
    sort_alpha: "A-Z",
    sort_type: "Type",
    suggest_cta: (q) => `Proposer « ${q} » à l'équipe geoVINUM`,
    suggest_hint: "Cette fiche n'existe pas encore. Signalez-la à Audrey — elle sera priorisée si plusieurs visiteurs la demandent.",
    suggest_sent: "Merci, votre suggestion a bien été enregistrée",
  },
  en: {
    intro: "Wine insights, guides, appellations, grape varieties and producers — all geoVINUM editorial in one place.",
    placeholder: "Search an article, appellation, grape or producer…",
    all: "All",
    appellations: "Appellations",
    cepages: "Grape varieties",
    producers: "Producers",
    results: (n) => `${n} result${n > 1 ? "s" : ""}`,
    no_query: (q) => `No results for “${q}”`,
    sort_label: "Sort by",
    sort_recent: "Recent",
    sort_alpha: "A-Z",
    sort_type: "Type",
    suggest_cta: (q) => `Suggest “${q}” to the geoVINUM team`,
    suggest_hint: "This fiche doesn't exist yet. Suggest it — it will be prioritised if several visitors request it.",
    suggest_sent: "Thanks, your suggestion has been recorded",
  },
  es: {
    intro: "Insights, guías, denominaciones, uvas y productores — todo el editorial geoVINUM en un solo lugar.",
    placeholder: "Buscar un artículo, denominación, uva o productor…",
    all: "Todo",
    appellations: "Denominaciones",
    cepages: "Uvas",
    producers: "Productores",
    results: (n) => `${n} resultado${n > 1 ? "s" : ""}`,
    no_query: (q) => `Sin resultados para «${q}»`,
    sort_label: "Ordenar por",
    sort_recent: "Reciente",
    sort_alpha: "A-Z",
    sort_type: "Tipo",
    suggest_cta: (q) => `Sugerir «${q}» al equipo geoVINUM`,
    suggest_hint: "Esta ficha aún no existe. Sugiérala — será priorizada si varios visitantes la piden.",
    suggest_sent: "Gracias, tu sugerencia se ha registrado",
  },
  pt: {
    intro: "Insights, guias, apelações, castas e produtores — todo o editorial geoVINUM num só sítio.",
    placeholder: "Pesquisar um artigo, apelação, casta ou produtor…",
    all: "Tudo",
    appellations: "Apelações",
    cepages: "Castas",
    producers: "Produtores",
    results: (n) => `${n} resultado${n > 1 ? "s" : ""}`,
    no_query: (q) => `Sem resultados para «${q}»`,
    sort_label: "Ordenar por",
    sort_recent: "Recente",
    sort_alpha: "A-Z",
    sort_type: "Tipo",
    suggest_cta: (q) => `Sugerir «${q}» à equipa geoVINUM`,
    suggest_hint: "Esta ficha ainda não existe. Sugira-a — será priorizada se vários visitantes a pedirem.",
    suggest_sent: "Obrigado, a sua sugestão foi registada",
  },
};

// Sort keys shared with the URL `sort` param.
const SORT_RECENT = "recent";
const SORT_ALPHA = "alpha";
const SORT_TYPE = "type";
// Priority used when sorting by type — articles first, then Atlas fiches.
const TYPE_ORDER = {
  wine_insights: 0, guides: 1, get_started: 2, for_professionals: 3, ebook: 4,
  appellation: 10, cepage: 11, producer: 12,
};

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const BlogPage = () => {
  const t = useT();
  const lang = useLang();
  const L = LOCAL_STRINGS[lang] || LOCAL_STRINGS.fr;
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "";
  const q = params.get("q") || "";
  const sort = params.get("sort") || SORT_RECENT;
  const [searchInput, setSearchInput] = useState(q);

  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(true);
  const [appellations, setAppellations] = useState([]);
  const [cepages, setCepages] = useState([]);
  const [producers, setProducers] = useState([]);
  // Live-filter debounced query — differs from the URL `q` (which is what
  // we push on submit for sharable/bookmarkable links).
  const [debouncedQ, setDebouncedQ] = useState(q);
  useEffect(() => {
    const h = setTimeout(() => setDebouncedQ(searchInput.trim()), 180);
    return () => clearTimeout(h);
  }, [searchInput]);

  // Fetch upstream Atlas indices once. These are language-agnostic (data
  // lives in one place; translations come from AtlasBlocksSection on the
  // detail page).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [appRes, cepRes, prodRes] = await Promise.all([
        apiFetch("/api/public/appellations?limit=500"),
        apiFetch("/api/public/cepages?limit=500"),
        apiFetch("/api/public/producers?limit=500"),
      ]);
      if (cancelled) return;
      if (appRes.ok) setAppellations(appRes.data?.items || []);
      if (cepRes.ok) setCepages(cepRes.data?.items || []);
      if (prodRes.ok) setProducers(prodRes.data?.items || []);
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch blog articles filtered by visitor language.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setArticlesLoading(true);
      const sp = new URLSearchParams();
      if (lang) sp.set("language", lang);
      sp.set("per_page", "200");
      const { ok, data } = await apiFetch(`/api/blog/articles?${sp}`);
      if (!cancelled) {
        setArticles(ok ? (data?.items || []) : []);
        setArticlesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lang]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (searchInput.trim()) next.set("q", searchInput.trim());
    else next.delete("q");
    setParams(next);
  };

  // ── Unified item shape ────────────────────────────────────────────────
  // Every content type is projected onto the same interface so the grid
  // can render them side-by-side without branching per-card.
  const allItems = useMemo(() => {
    const items = [];
    // Blog articles
    articles.forEach((a) => {
      items.push({
        id: `article-${a.slug}`,
        kind: a.category || "wine_insights",
        kindLabel: t(`blogP.cat.${a.category || "wine_insights"}`) || a.category || "",
        title: a.title || "",
        subtitle: (a.excerpt || "").slice(0, 140),
        image: a.cover_image_url || "",
        coverQuery: "",
        coverKind: "",
        href: `/blog/${a.slug}`,
        readMinutes: a.read_minutes || null,
        author: a.author_name || "",
        publishedAt: a.published_at || "",
      });
    });
    // Appellations
    appellations.forEach((a) => {
      items.push({
        id: `appellation-${a.slug}`,
        kind: KIND_APPELLATION,
        kindLabel: L.appellations,
        title: a.name || "",
        subtitle: [a.region, a.country].filter(Boolean).join(" · "),
        image: "",
        coverQuery: a.name,
        coverKind: "appellation",
        href: `/explore/${a.slug}`,
      });
    });
    // Cépages
    cepages.forEach((c) => {
      items.push({
        id: `cepage-${c.slug}`,
        kind: KIND_CEPAGE,
        kindLabel: L.cepages,
        title: c.name || "",
        subtitle: [c.color, c.origin].filter(Boolean).join(" · "),
        image: "",
        coverQuery: c.name,
        coverKind: "cepage",
        href: `/cepage/${c.slug}`,
      });
    });
    // Producteurs
    producers.forEach((p) => {
      items.push({
        id: `producer-${p.slug}`,
        kind: KIND_PRODUCER,
        kindLabel: L.producers,
        title: p.name || "",
        subtitle: [p.appellation, p.region, p.country].filter(Boolean).join(" · "),
        image: "",
        coverQuery: `${p.name}${p.region ? ` ${p.region}` : ""}`,
        coverKind: "producer",
        href: `/producteur/${p.slug}`,
      });
    });
    return items;
  }, [articles, appellations, cepages, producers, t, L]);

  // Available filter chips derived from the merged dataset. Editorial
  // categories only appear if at least one article uses them, which keeps
  // the chip row lean when the blog is still small.
  const chips = useMemo(() => {
    const counts = {};
    allItems.forEach((it) => {
      counts[it.kind] = (counts[it.kind] || 0) + 1;
    });
    const editorial = CATEGORY_SLUGS
      .filter((slug) => counts[slug])
      .map((slug) => ({ slug, label: t(`blogP.cat.${slug}`), count: counts[slug] }));
    const atlas = [
      { slug: KIND_APPELLATION, label: L.appellations, count: counts[KIND_APPELLATION] || 0 },
      { slug: KIND_CEPAGE, label: L.cepages, count: counts[KIND_CEPAGE] || 0 },
      { slug: KIND_PRODUCER, label: L.producers, count: counts[KIND_PRODUCER] || 0 },
    ].filter((c) => c.count > 0);
    return { editorial, atlas, total: allItems.length };
  }, [allItems, t, L]);

  // Live-filter the grid — reads from debouncedQ (updates as you type) so
  // results shrink instantly without waiting for a form submit.
  const filteredItems = useMemo(() => {
    const nq = norm(debouncedQ);
    const filtered = allItems.filter((it) => {
      if (cat && it.kind !== cat) return false;
      if (nq) {
        const hay = norm(`${it.title} ${it.subtitle} ${it.kindLabel}`);
        if (!hay.includes(nq)) return false;
      }
      return true;
    });
    // Apply the selected sort. `Recent` uses published_at (falls back to
    // upstream order for Atlas fiches without a date). `A-Z` is a pure
    // alpha sort. `Type` groups by kind priority then alpha inside each.
    if (sort === SORT_ALPHA) {
      filtered.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
    } else if (sort === SORT_TYPE) {
      filtered.sort((a, b) => {
        const ka = TYPE_ORDER[a.kind] ?? 99;
        const kb = TYPE_ORDER[b.kind] ?? 99;
        if (ka !== kb) return ka - kb;
        return (a.title || "").localeCompare(b.title || "");
      });
    } else {
      // SORT_RECENT — dated items desc, then undated in stable insertion order.
      filtered.sort((a, b) => {
        const da = a.publishedAt || "";
        const db = b.publishedAt || "";
        if (da && db) return db.localeCompare(da);
        if (da) return -1;
        if (db) return 1;
        return 0;
      });
    }
    return filtered;
  }, [allItems, cat, debouncedQ, sort]);

  const setChip = (slug) => {
    const next = new URLSearchParams(params);
    if (slug) next.set("cat", slug);
    else next.delete("cat");
    setParams(next);
  };

  const setSort = (value) => {
    const next = new URLSearchParams(params);
    if (value && value !== SORT_RECENT) next.set("sort", value);
    else next.delete("sort");
    setParams(next);
  };

  return (
    <Layout testId="page-blog">
      <SEO title={t("blogP.seo.title")} description={t("blogP.seo.desc")} />

      {/* HERO */}
      <section className="bg-[color:var(--gv-mist)] pt-12 pb-10">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <Breadcrumb items={[{ to: "/", label: t("blogP.bc.home") }, { label: EXPLORER_HERO[lang]?.title || EXPLORER_HERO.fr.title }]} />
          <h1 className="font-display text-4xl md:text-5xl text-[color:var(--gv-ink)] mt-6 mb-3" data-testid="blog-title">
            {EXPLORER_HERO[lang]?.title || EXPLORER_HERO.fr.title}
          </h1>
          <p className="text-[15px] text-[color:var(--gv-ink)]/75 max-w-2xl leading-relaxed">
            {EXPLORER_HERO[lang]?.subtitle || EXPLORER_HERO.fr.subtitle}
          </p>

          {/* Big search bar */}
          <form onSubmit={onSearchSubmit} className="mt-6 relative max-w-2xl">
            <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[color:var(--gv-ink)]/40" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={T(t, "blogP.search.placeholder_unified", L.placeholder)}
              className="w-full h-12 pl-10 pr-24 bg-white border border-[color:var(--gv-line)] rounded-[6px] text-[15px] focus:border-[color:var(--gv-pink)]/50 focus:shadow-sm outline-none transition-all"
              data-testid="blog-search-input"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput("");
                  const next = new URLSearchParams(params);
                  next.delete("q");
                  setParams(next);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[color:var(--gv-ink)]/40 hover:text-[color:var(--gv-ink)]"
                data-testid="blog-search-clear"
                aria-label="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </form>

          {/* Tag chips + sort dropdown — chips left, sort right on desktop. */}
          <div className="mt-6 flex items-center justify-between gap-4 flex-wrap" data-testid="blog-controls">
            <div className="flex items-center gap-2 flex-wrap" data-testid="blog-chips">
              <Chip
                label={L.all}
                count={chips.total}
                active={!cat}
                onClick={() => setChip("")}
                testid="blog-chip-all"
              />
              {chips.editorial.map((c) => (
                <Chip
                  key={c.slug}
                  label={c.label}
                  count={c.count}
                  active={cat === c.slug}
                  onClick={() => setChip(c.slug)}
                  testid={`blog-chip-${c.slug}`}
                />
              ))}
              {chips.atlas.length > 0 && (
                <span className="w-px h-5 bg-[color:var(--gv-line)] mx-1" aria-hidden />
              )}
              {chips.atlas.map((c) => (
                <Chip
                  key={c.slug}
                  label={c.label}
                  count={c.count}
                  active={cat === c.slug}
                  onClick={() => setChip(c.slug)}
                  testid={`blog-chip-${c.slug}`}
                  atlas
                  Icon={c.slug === KIND_APPELLATION ? MapPin : c.slug === KIND_CEPAGE ? Grape : Building2}
                />
              ))}
            </div>
            <SortDropdown value={sort} onChange={setSort} L={L} />
          </div>
        </div>
      </section>

      {/* UNIFIED GRID */}
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          {articlesLoading && filteredItems.length === 0 && (
            <div className="text-center py-16 text-[color:var(--gv-ink)]/50">
              {t("blogP.loading")}
            </div>
          )}
          {!articlesLoading && filteredItems.length === 0 && (
            <div className="text-center py-16" data-testid="blog-empty">
              <FileText size={32} className="mx-auto text-[color:var(--gv-ink)]/30 mb-4" />
              <div className="font-display text-xl text-[color:var(--gv-ink)]">
                {t("blogP.empty.title")}
              </div>
              {debouncedQ && (
                <div className="text-[13px] text-[color:var(--gv-ink)]/55 mt-2">
                  {L.no_query(debouncedQ)}
                </div>
              )}
              {debouncedQ && debouncedQ.length >= 3 && (
                <SuggestButton query={debouncedQ} lang={lang} cat={cat} L={L} />
              )}
            </div>
          )}
          {filteredItems.length > 0 && (
            <>
              <div className="mb-6 text-[12.5px] text-[color:var(--gv-ink)]/50" data-testid="blog-results-count">
                {L.results(filteredItems.length)}
                {cat && ` · ${chips.editorial.find((c) => c.slug === cat)?.label || chips.atlas.find((c) => c.slug === cat)?.label || cat}`}
                {debouncedQ && ` · « ${debouncedQ} »`}
              </div>
              <div key={`grid-${cat || "all"}-${debouncedQ || ""}-${sort}`} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="blog-grid-unified">
                {filteredItems.map((it) => <UnifiedCard key={it.id} item={it} />)}
              </div>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
};

// ── Sub-components ─────────────────────────────────────────────────────

const Chip = ({ label, count, active, onClick, testid, atlas = false, Icon }) => (
  <button
    onClick={onClick}
    className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] text-[12.5px] transition-colors ${
      active
        ? atlas ? "bg-[color:var(--gv-pink)] text-white" : "bg-[color:var(--gv-ink)] text-white"
        : "bg-white text-[color:var(--gv-ink)] border border-[color:var(--gv-line)] hover:border-[color:var(--gv-ink)]/30"
    }`}
    data-testid={testid}
    aria-pressed={active}
  >
    {Icon && <Icon size={11} className={active ? "opacity-90" : "opacity-60"} />}
    {label}
    <span className={`text-[11px] ${active ? "opacity-70" : "opacity-45"}`}>({count})</span>
  </button>
);

const SortDropdown = ({ value, onChange, L }) => (
  <label className="shrink-0 inline-flex items-center gap-2 text-[12.5px] text-[color:var(--gv-ink)]/60" data-testid="blog-sort-wrapper">
    <ArrowDownUp size={12} className="opacity-70" />
    <span className="hidden sm:inline">{L.sort_label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-8 pl-2 pr-7 rounded-[5px] bg-white border border-[color:var(--gv-line)] text-[12.5px] text-[color:var(--gv-ink)] hover:border-[color:var(--gv-ink)]/30 focus:border-[color:var(--gv-pink)]/50 outline-none cursor-pointer appearance-none bg-no-repeat"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3e%3cpath fill='none' stroke='%23213547' stroke-width='1.5' d='M1 1l4 4 4-4'/%3e%3c/svg%3e\")",
        backgroundPosition: "right 8px center",
      }}
      data-testid="blog-sort-select"
    >
      <option value={SORT_RECENT}>{L.sort_recent}</option>
      <option value={SORT_ALPHA}>{L.sort_alpha}</option>
      <option value={SORT_TYPE}>{L.sort_type}</option>
    </select>
  </label>
);

const KIND_META = {
  [KIND_APPELLATION]: { color: "text-[color:var(--gv-pink)]", Icon: MapPin },
  [KIND_CEPAGE]: { color: "text-[color:var(--gv-pink)]", Icon: Grape },
  [KIND_PRODUCER]: { color: "text-[color:var(--gv-pink)]", Icon: Building2 },
};

// SuggestButton — offers the visitor to push a zero-result query into
// Audrey's lead queue. We POST once per (query, session) via a
// ref-tracked set so a user who keeps typing doesn't spam the backend.
// The button auto-submits after a 1.2 s idle timer AND also on click, so
// signal is captured even if the visitor never clicks anything.
//
// kind_intent is inferred from the visitor's active filter chip:
//   cat=producer   → producer
//   cat=appellation→ appellation
//   cat=cepage     → cepage
//   otherwise      → any   (Audrey classifies from the admin panel)
const SuggestButton = ({ query, lang, cat, L }) => {
  const [state, setState] = useState("idle");
  const sentRef = useRef(new Set());
  const kindIntent =
    cat === "producer" ? "producer"
    : cat === "appellation" ? "appellation"
    : cat === "cepage" ? "cepage"
    : "any";

  const submit = async (source) => {
    const key = query.toLowerCase().trim();
    if (!key || sentRef.current.has(key)) return;
    sentRef.current.add(key);
    setState("sending");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/producer-leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query, source, lang, kind_intent: kindIntent }),
      });
      setState(res.ok ? "sent" : "error");
    } catch {
      setState("error");
    }
  };

  useEffect(() => {
    const h = setTimeout(() => submit("blog"), 1200);
    return () => clearTimeout(h);
  }, [query]);

  if (state === "sent") {
    return (
      <div className="mt-4 inline-flex items-center gap-2 text-[12.5px] text-[color:var(--gv-ink)]/60" data-testid="blog-suggest-sent">
        <Check size={13} className="text-[color:var(--gv-pink)]" />
        {L.suggest_sent}
      </div>
    );
  }
  return (
    <div className="mt-6 max-w-lg mx-auto text-center">
      <p className="text-[12.5px] text-[color:var(--gv-ink)]/55 mb-3">{L.suggest_hint}</p>
      <button
        type="button"
        onClick={() => submit("blog")}
        disabled={state === "sending"}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-[5px] bg-[color:var(--gv-pink)] text-white text-[13px] font-medium hover:bg-[color:var(--gv-ink)] transition-colors disabled:opacity-60"
        data-testid="blog-suggest-cta"
      >
        <Send size={13} />
        {L.suggest_cta(query)}
      </button>
    </div>
  );
};

const UnifiedCard = ({ item }) => {
  const meta = KIND_META[item.kind];
  const testId = `unified-card-${item.kind}-${item.id.split("-").slice(1).join("-")}`;
  // Track img failures so we can gracefully fall back to the Wikimedia
  // lookup. This fixes the "images load then disappear" bug on prod: our
  // article thumbnails are generated on the local pod and return 404
  // when the frontend is served from Netlify → Railway, so we keep an
  // editorial cover as a hard-coded safety net for every card.
  const [imgFailed, setImgFailed] = useState(false);
  const useDirectImage = item.image && !imgFailed;
  return (
    <Link
      to={item.href}
      className="group block bg-white border border-[color:var(--gv-line)] rounded-[6px] overflow-hidden hover:border-[color:var(--gv-pink)] hover:shadow-md transition-all"
      data-testid={testId}
    >
      {useDirectImage ? (
        <div className="aspect-[16/10] bg-[color:var(--gv-mist)] overflow-hidden">
          <img
            src={item.image}
            alt={item.title}
            loading="lazy"
            onError={() => setImgFailed(true)}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
          />
        </div>
      ) : (
        <CoverImage
          // When the article thumbnail failed, use the article title as
          // the Wikimedia query — surprisingly good match rate for wine
          // articles (e.g. "Chablis", "Barolo", "Pinot Noir").
          query={item.coverQuery || item.title}
          kind={item.coverKind || "appellation"}
          aspectClass="aspect-[16/10]"
          testId={`${testId}-cover`}
        />
      )}
      <div className="p-4">
        <div className={`text-[10.5px] uppercase tracking-widest mb-2 inline-flex items-center gap-1.5 ${meta ? meta.color : "text-[color:var(--gv-pink)]"}`}>
          {meta?.Icon && <meta.Icon size={10} />}
          {item.kindLabel}
        </div>
        <div className="font-display text-[17px] leading-tight text-[color:var(--gv-ink)] group-hover:text-[color:var(--gv-pink)] mb-2 line-clamp-2">
          {item.title}
        </div>
        {item.subtitle && (
          <div className="text-[12.5px] text-[color:var(--gv-ink)]/60 line-clamp-2">
            {item.subtitle}
          </div>
        )}
        {(item.readMinutes || item.author) && (
          <div className="mt-3 pt-3 border-t border-[color:var(--gv-line)]/60 flex items-center gap-3 text-[11px] text-[color:var(--gv-ink)]/45">
            {item.readMinutes && (
              <span className="inline-flex items-center gap-1"><Clock size={10} /> {item.readMinutes} min</span>
            )}
            {item.author && <span>· {item.author}</span>}
          </div>
        )}
      </div>
    </Link>
  );
};

export default BlogPage;
